import React, { ReactNode, useEffect, useState } from 'react';

import { Amplify } from 'aws-amplify';
import {
    confirmResetPassword,
    confirmSignIn,
    fetchAuthSession,
    fetchMFAPreference,
    resetPassword,
    setUpTOTP,
    signIn,
    signInWithRedirect,
    signOut,
    updateMFAPreference,
    verifyTOTPSetup,
} from 'aws-amplify/auth';
import { cognitoUserPoolsTokenProvider } from 'aws-amplify/auth/cognito';
/**
 * Ce qui ECHANGE le code d'autorisation contre des jetons au retour du fournisseur
 * d'identite (PCG-304). Sans cet import, le retour se pose sur `/login` avec un `?code=`
 * dans l'adresse et **rien ne se passe** : pas d'erreur, pas de session, un ecran de
 * connexion qui a l'air de n'avoir jamais rien tente.
 *
 * Il est ici et non dans l'application parce que c'est le seul endroit ou Amplify est
 * configure : importe ailleurs, il s'executerait peut-etre avant `Amplify.configure`.
 */
import 'aws-amplify/auth/enable-oauth-listener';
import { CookieStorage, Hub } from 'aws-amplify/utils';

import { fetch, getBaseDomain, getInsideApiUrl, isLocalhost } from '@freelancelabs/shared';

import { useInstance } from '../../hooks';
import AuthenticationContext from './authenticationContext';
import { ChallengeName, ForgotPasswordSubmitInput, LoginCheckInput, LoginInput } from './interface';

interface Props {
    children?: ReactNode;
    /** Inutilisee depuis amplify v6 (la region est deduite du user pool id), conservee pour compat */
    awsCognitoRegion: string;
    awsUserPoolsId: string;
    awsUserPoolsWebClientId: string;
    /**
     * Le domaine heberge Cognito (`develop-inside.auth.eu-central-1.amazoncognito.com`),
     * par lequel passe la redirection vers le fournisseur d'identite (PCG-304).
     *
     * **Optionnelle, et sans elle le SSO n'est simplement pas monte.** Les trois
     * applications montent ce provider ; seule celle qui fait entrer les gens a besoin de
     * l'OAuth. La rendre obligatoire ferait declarer aux deux autres une valeur dont elles
     * n'ont aucun usage.
     */
    awsOauthDomain?: string;
}

const isSecureCookie = () => !isLocalhost();

// mapping des etapes amplify v6 vers l'API publique historique du provider
const SIGN_IN_STEP_TO_CHALLENGE: Record<string, ChallengeName> = {
    CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED: 'NEW_PASSWORD_REQUIRED',
    CONFIRM_SIGN_IN_WITH_CUSTOM_CHALLENGE: 'CUSTOM_CHALLENGE',
    CONFIRM_SIGN_IN_WITH_EMAIL_CODE: 'EMAIL_OTP',
    CONFIRM_SIGN_IN_WITH_TOTP_CODE: 'TOTP_CODE',
};

/** Nom affiche dans les applications d'authentification (Google Authenticator, etc.) */
const TOTP_ISSUER = 'Freelance.com';

const AuthenticationProvider = ({ children, awsUserPoolsWebClientId, awsUserPoolsId, awsOauthDomain }: Props) => {
    const [isInitialized, setIsInitialized] = useState(false);
    const [hasSession, setHasSession] = useState(false);

    useEffect(() => {
        const run = async () => {
            try {
                const { tokens } = await fetchAuthSession();
                setHasSession(Boolean(tokens?.idToken));
            } catch {
                setHasSession(false);
            } finally {
                setIsInitialized(true);
            }
        };

        run();
    }, []);

    /**
     * LE RETOUR DU FOURNISSEUR D'IDENTITE, ET POURQUOI L'EFFET CI-DESSUS NE SUFFIT PAS
     * (PCG-304).
     *
     * L'echange du code contre des jetons est **asynchrone** : au retour sur `/login`,
     * l'effet de montage lit la session AVANT que les jetons existent, conclut « personne »,
     * et la personne reste devant l'ecran de connexion alors qu'elle vient de s'authentifier.
     * Rien n'echoue, et c'est exactement ce qui rend le defaut difficile a nommer.
     *
     * L'ancien depot ne posait pas cet ecouteur non plus.
     *
     * `signInWithRedirect_failure` est l'autre moitie, et elle n'est pas facultative : un
     * retour qui rate sans un mot laisse la personne devant le formulaire qu'elle vient de
     * quitter, persuadee d'avoir mal clique.
     */
    useEffect(() => {
        const stop = Hub.listen('auth', ({ payload }) => {
            if (payload.event === 'signInWithRedirect') {
                setHasSession(true);
            }
            if (payload.event === 'signInWithRedirect_failure') {
                setHasSession(false);
            }
        });
        return stop;
    }, []);

    const createAuthentication = () => {
        Amplify.configure({
            Auth: {
                Cognito: {
                    userPoolId: awsUserPoolsId,
                    userPoolClientId: awsUserPoolsWebClientId,
                    /**
                     * Attention, LA CIBLE EST `/login` ET SURTOUT PAS `/login/check`
                     * (PCG-304). Cette derniere n'est pas dans les `CallbackURLs` du client
                     * Cognito, et sa page navigue vers `/login` des que le parametre `email`
                     * est absent, ce que le retour OAuth est toujours : il porte `code` et
                     * `state`. Elle partirait donc en plein echange. L'ancien depot visait
                     * `/login/check` en dur.
                     *
                     * L'adresse se calcule et ne s'ecrit pas : `window.location.origin`
                     * suit l'environnement sans qu'une liste ait a etre tenue a jour.
                     */
                    ...(awsOauthDomain
                        ? {
                              loginWith: {
                                  oauth: {
                                      domain: awsOauthDomain,
                                      scopes: ['email', 'openid', 'aws.cognito.signin.user.admin'],
                                      redirectSignIn: [`${window.location.origin}/login`],
                                      redirectSignOut: [`${window.location.origin}/login`],
                                      responseType: 'code' as const,
                                  },
                              },
                          }
                        : {}),
                },
            },
        });
        cognitoUserPoolsTokenProvider.setKeyValueStorage(
            new CookieStorage({
                domain: getBaseDomain(),
                secure: isSecureCookie(),
            })
        );

        const getAuthorizationToken = async (): Promise<string | null> => {
            try {
                const { tokens } = await fetchAuthSession();
                return tokens?.idToken?.toString() ?? null;
            } catch {
                return null;
            }
        };

        const completeNewPassword = async (password: string): Promise<void> => {
            const { isSignedIn } = await confirmSignIn({ challengeResponse: password });
            setHasSession(isSignedIn);
        };

        const confirmMfaCode = async (code: string): Promise<void> => {
            const { isSignedIn } = await confirmSignIn({ challengeResponse: code });

            if (!isSignedIn) {
                throw new Error('Invalid or expired code', { cause: { code: 401 } });
            }
            setHasSession(true);
        };

        const fetchMfaSettings = async (): Promise<{ email: boolean; totp: boolean }> => {
            const { enabled } = await fetchMFAPreference();
            return {
                email: enabled?.includes('EMAIL') ?? false,
                totp: enabled?.includes('TOTP') ?? false,
            };
        };

        const fetchEmailMfaEnabled = async (): Promise<boolean> => {
            const { email } = await fetchMfaSettings();
            return email;
        };

        const setEmailMfaEnabled = async (enabled: boolean): Promise<void> => {
            await updateMFAPreference({ email: enabled ? 'PREFERRED' : 'DISABLED' });
        };

        const setupTotp = async (accountName: string): Promise<{ secret: string; uri: string }> => {
            const details = await setUpTOTP();
            return {
                secret: details.sharedSecret,
                uri: details.getSetupUri(TOTP_ISSUER, accountName).toString(),
            };
        };

        const verifyTotpSetup = async (code: string): Promise<void> => {
            await verifyTOTPSetup({ code });
            // le TOTP devient la methode preferee (l'email OTP eventuel reste en secours)
            await updateMFAPreference({ totp: 'PREFERRED' });
        };

        const disableTotp = async (): Promise<void> => {
            await updateMFAPreference({ totp: 'DISABLED' });
        };

        const login = async ({ email, password }: LoginInput): Promise<ChallengeName | undefined> => {
            const { isSignedIn, nextStep } = await signIn({
                username: email,
                password,
                options: {
                    // le trigger custom-message d'inside-api n'applique le template
                    // html (mjml) que si useCommon vaut 'true' - meme convention que
                    // le passwordless ; couvre l'envoi du code MFA email
                    clientMetadata: { useCommon: 'true' },
                },
            });

            // plusieurs methodes MFA sans preference : on choisit la plus sure (TOTP)
            if (nextStep.signInStep === 'CONTINUE_SIGN_IN_WITH_MFA_SELECTION') {
                const type = nextStep.allowedMFATypes?.includes('TOTP') ? 'TOTP' : 'EMAIL';
                const selection = await confirmSignIn({ challengeResponse: type });
                return SIGN_IN_STEP_TO_CHALLENGE[selection.nextStep.signInStep];
            }

            const challenge = SIGN_IN_STEP_TO_CHALLENGE[nextStep.signInStep];
            if (challenge) {
                return challenge;
            }

            setHasSession(isSignedIn);
        };

        const passwordLessLoginCheck = async ({ code, email }: LoginCheckInput): Promise<void> => {
            await signIn({ username: email, options: { authFlowType: 'CUSTOM_WITHOUT_SRP' } });
            const { isSignedIn } = await confirmSignIn({ challengeResponse: code });

            if (isSignedIn) {
                setHasSession(true);
                return;
            }

            throw new Error('Invalid or expired code', { cause: { code: 401 } });
        };

        const passwordLessLogin = async (email: string): Promise<void> => {
            await fetch({
                method: 'post',
                url: `${getInsideApiUrl('auth/passwordless')}`,
                data: {
                    email,
                    clientId: import.meta.env.PUBLIC_AWS_USER_POOLS_WEB_CLIENT_ID,
                    validationUrl: `${window.location.origin}/login/check`,
                },
            });
        };

        /**
         * Part vers le fournisseur d'identite (PCG-304).
         *
         * Attention, LA PAGE EST REMPLACEE : rien ne s'execute apres, et il ne faut donc
         * poser aucun etat « c'est fini » derriere cet appel. Ce qui revient plus tard est un
         * chargement neuf sur `/login`, ou l'ecouteur du Hub prend le relais.
         *
         * Attention, ELLE LEVE QUAND L'OAUTH N'EST PAS MONTE plutot que de ne rien faire :
         * sans `awsOauthDomain`, Amplify n'a aucune configuration OAuth et l'appel echouerait
         * de toute facon, mais avec un message qui parle de sa propre plomberie. Un bouton
         * qui ne repond pas se lit comme une panne de reseau ; l'appelant doit pouvoir dire
         * ce qui manque vraiment.
         */
        const signInWithSso = async (providerName: string): Promise<void> => {
            if (!awsOauthDomain) {
                throw new Error('SSO is not configured on this application', { cause: { code: 501 } });
            }
            await signInWithRedirect({ provider: { custom: providerName } });
        };

        /**
         * La session en cours vient-elle d'un fournisseur externe ?
         *
         * Elle se lit sur `identities` du jeton d'identite, que Cognito pose UNIQUEMENT sur
         * un utilisateur federe. Attention, C'EST UN FAIT DE LA SESSION ET NON DE LA
         * PERSONNE : la meme personne revenue par mot de passe rend `false`, et c'est ce
         * qu'on veut, puisque ce qui dispense du second facteur est celui que le fournisseur
         * a deja demande sur CE chemin-la.
         *
         * **Elle repond `false` quand elle ne sait pas**, et c'est le sens sur : une lecture
         * ratee ne doit pas faire sauter une barriere de securite.
         */
        const isFederatedSession = async (): Promise<boolean> => {
            try {
                const { tokens } = await fetchAuthSession();
                const identities = tokens?.idToken?.payload?.identities;
                return Array.isArray(identities) && identities.length > 0;
            } catch {
                return false;
            }
        };

        const logout = async () => {
            await signOut();
            setHasSession(false);
            // @TODO remove cookie session, redirect or CB ?
        };

        const forgotPassword = (username: string) =>
            resetPassword({
                username,
                options: {
                    clientMetadata: {
                        useCommon: 'true',
                        clientId: import.meta.env.PUBLIC_AWS_USER_POOLS_WEB_CLIENT_ID,
                        validationUrl: `${window.location.origin}/update-password`,
                        forgotPasswordUrl: `${window.location.origin}/reset-password`,
                    },
                },
            }).then(() => undefined);

        const forgotPasswordSubmit = ({ email, code, newPassword }: ForgotPasswordSubmitInput) =>
            confirmResetPassword({ username: email, confirmationCode: code, newPassword });

        return {
            login,
            signInWithSso,
            isFederatedSession,
            logout,
            completeNewPassword,
            confirmMfaCode,
            fetchEmailMfaEnabled,
            fetchMfaSettings,
            setEmailMfaEnabled,
            setupTotp,
            verifyTotpSetup,
            disableTotp,
            forgotPassword,
            forgotPasswordSubmit,
            passwordLessLogin,
            passwordLessLoginCheck,
            getAuthorizationToken,
        };
    };

    const auth = useInstance(() => {
        return createAuthentication();
    });

    return (
        <AuthenticationContext.Provider value={{ ...auth, hasSession, isInitialized }}>
            {children}
        </AuthenticationContext.Provider>
    );
};

export default AuthenticationProvider;
